import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../../../i18n/useTranslation';
import { ChevronDown } from 'lucide-react';
import { Button } from '../../atoms/Button/Button';
import { useActiveAquarium } from '../../../contexts/AquariumContext';
import type { Aquarium } from '../../../types/aquarium';
import { AquariumSwitcherItem } from './AquariumSwitcherItem';
import './AquariumSwitcher.css';

export function AquariumSwitcher() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { activeAquarium, aquariums, setActiveAquarium } = useActiveAquarium();

  const activeAquariumId = activeAquarium?.id ?? null;

  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);

  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listboxRef = useRef<HTMLDivElement>(null);
  const listboxId = 'aquarium-switcher-listbox';
  const triggerId = 'aquarium-switcher-trigger';

  const hasAquariums = aquariums.length > 0;

  const getOptionId = useCallback((index: number): string => `aquarium-switcher-option-${index}`, []);

  const closeDropdown = useCallback((focusTrigger: boolean = true): void => {
    setIsOpen(false);
    setHighlightedIndex(-1);
    if (focusTrigger) {
      triggerRef.current?.focus();
    }
  }, []);

  const openDropdown = useCallback((): void => {
    setIsOpen(true);
    const activeIndex =
      activeAquariumId != null ? aquariums.findIndex((a: Aquarium) => a.id === activeAquariumId) : -1;
    setHighlightedIndex(activeIndex >= 0 ? activeIndex : 0);
  }, [aquariums, activeAquariumId]);

  const toggleDropdown = useCallback((): void => {
    if (isOpen) {
      closeDropdown(false);
    } else {
      openDropdown();
    }
  }, [isOpen, openDropdown, closeDropdown]);

  const handleSelect = useCallback(
    (id: string): void => {
      setActiveAquarium(id);
      closeDropdown();
    },
    [setActiveAquarium, closeDropdown],
  );

  const handleAddAquarium = useCallback((): void => {
    closeDropdown();
    navigate('/aquariums/new');
  }, [closeDropdown, navigate]);

  // The aquarium list ("Mehr" → Aquarien), one click away from every page.
  const handleManageAquariums = useCallback((): void => {
    closeDropdown();
    navigate('/aquariums');
  }, [closeDropdown, navigate]);

  // Outside click to close
  useEffect(() => {
    if (!isOpen) return;

    function handleOutsideClick(event: MouseEvent): void {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        closeDropdown(false);
      }
    }

    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [isOpen, closeDropdown]);

  // Move focus to the listbox exactly once when it opens — not on every
  // highlight change — to avoid fighting with arrow-key navigation state.
  useEffect(() => {
    if (isOpen && hasAquariums) {
      listboxRef.current?.focus();
    }
  }, [isOpen, hasAquariums]);

  const handleTriggerKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLButtonElement>): void => {
      if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        if (!isOpen) {
          openDropdown();
        }
      }
    },
    [isOpen, openDropdown],
  );

  const handleListKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>): void => {
      const optionCount = aquariums.length;

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setHighlightedIndex(prev => (prev < optionCount - 1 ? prev + 1 : 0));
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        setHighlightedIndex(prev => (prev > 0 ? prev - 1 : optionCount - 1));
      } else if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < optionCount) {
          const selected = aquariums[highlightedIndex];
          if (selected) {
            handleSelect(selected.id);
          }
        }
      } else if (event.key === 'Escape') {
        event.preventDefault();
        closeDropdown();
      } else if (event.key === 'Tab') {
        closeDropdown(false);
      }
    },
    [aquariums, highlightedIndex, handleSelect, closeDropdown],
  );

  const currentLabel = activeAquarium ? activeAquarium.name : t('header.aquariumSwitcher.selectAquarium');
  const activeDescendant =
    isOpen && highlightedIndex >= 0 ? getOptionId(highlightedIndex) : undefined;

  return (
    <div className="aquarium-switcher" ref={containerRef}>
      <Button
        type="button"
        variant="ghost"
        id={triggerId}
        ref={triggerRef}
        className="aquarium-switcher__trigger"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={t('header.aquariumSwitcher.currentAquarium', { name: currentLabel })}
        onClick={toggleDropdown}
        onKeyDown={handleTriggerKeyDown}
      >
        <span className="aquarium-switcher__label">{currentLabel}</span>
        <ChevronDown className="aquarium-switcher__chevron" aria-hidden="true" size={16} />
      </Button>

      {isOpen && (
        <div className="aquarium-switcher__popover">
          {hasAquariums ? (
            <div
              role="listbox"
              id={listboxId}
              aria-label={t('header.aquariumSwitcher.yourAquariums')}
              aria-activedescendant={activeDescendant}
              tabIndex={-1}
              className="aquarium-switcher__listbox"
              onKeyDown={handleListKeyDown}
              ref={listboxRef}
            >
              {aquariums.map((aquarium: Aquarium, index: number) => (
                <AquariumSwitcherItem
                  key={aquarium.id}
                  id={getOptionId(index)}
                  aquarium={{
                    id: aquarium.id,
                    name: aquarium.name,
                    volume: aquarium.grossVolume,
                    volumeUnit: 'liters',
                  }}
                  isSelected={aquarium.id === activeAquariumId}
                  isHighlighted={index === highlightedIndex}
                  onSelect={() => handleSelect(aquarium.id)}
                  onMouseEnter={() => setHighlightedIndex(index)}
                />
              ))}
            </div>
          ) : (
            <div className="aquarium-switcher__empty">{t('header.aquariumSwitcher.noAquariums')}</div>
          )}
          <Button
            type="button"
            variant="ghost"
            className="aquarium-switcher__add-action"
            onClick={handleAddAquarium}
          >
            {t('header.aquariumSwitcher.addAquarium')}
          </Button>
          <Button
            type="button"
            variant="ghost"
            className="aquarium-switcher__add-action"
            onClick={handleManageAquariums}
          >
            {t('header.aquariumSwitcher.manageAquariums')}
          </Button>
        </div>
      )}
    </div>
  );
}
